/**
 * GUI Schemas
 *
 * Zod schemas for types that originated in lib/gui/types.
 * These enable validation and type derivation for GUI entities.
 */

import { z } from "zod";
import {
  UuidField,
  TimestampField,
  MetadataField,
} from "./common";

// =============================================================================
// Workspace & UI Types
// =============================================================================

const WorkspaceType = z.enum(["code", "design", "notes", "chat", "media", "custom"]);

const Workspace = z.object({
  id: UuidField,
  name: z.string().min(1),
  type: WorkspaceType,
  color: z.string(),
  icon: z.string().optional(),
  description: z.string().optional(),
  createdAt: TimestampField,
  updatedAt: TimestampField,
});

const NotificationType = z.enum(["info", "success", "warning", "error"]);

const Notification = z.object({
  id: z.string(),
  type: NotificationType,
  title: z.string(),
  message: z.string().optional(),
  duration: z.number().optional(),
  createdAt: TimestampField,
});

// =============================================================================
// Data Source & File System
// =============================================================================

const DataSourceType = z.enum([
  "local-filesystem",
  "cloud-storage",
  "remote-url",
  "clipboard",
  "memory",
]);

const Environment = z.enum(["browser", "tauri", "chrome-extension", "unknown"]);

const DataSourceMetadata = z.object({
  // Local filesystem
  deviceId: z.string().optional(),
  diskId: z.string().optional(),
  fullPath: z.string().optional(),
  parentPath: z.string().optional(),
  // Cloud storage
  cloudProvider: z.string().optional(),
  cloudPath: z.string().optional(),
  // Remote URL
  url: z.string().optional(),
  domain: z.string().optional(),
  // Additional
  originalName: z.string().optional(),
  mimeType: z.string().optional(),
  encoding: z.string().optional(),
});

const DataSourceCapabilities = z.object({
  canRead: z.boolean(),
  canWrite: z.boolean(),
  canDelete: z.boolean(),
  canMove: z.boolean(),
  canCopy: z.boolean(),
});

const DataSource = z.object({
  type: DataSourceType,
  environment: Environment,
  metadata: DataSourceMetadata,
  capabilities: DataSourceCapabilities,
});

const FileItemType = z.enum(["file", "folder"]);

const FileItem = z.object({
  id: z.string(),
  name: z.string(),
  type: FileItemType,
  path: z.string().optional(),
  size: z.number().optional(),
  lastModified: TimestampField.optional(),
  parentId: z.string().optional(),
  isExpanded: z.boolean().optional(),
  dataSource: DataSource,
});

// =============================================================================
// Search System
// =============================================================================

const SearchResult = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  type: z.string(),
  icon: z.string().optional(),
  url: z.string().optional(),
  metadata: MetadataField,
  score: z.number(),
  searcherType: z.string(),
});

const SearcherConfig = z.object({
  type: z.string(),
  title: z.string(),
  icon: z.string().optional(),
  description: z.string().optional(),
  enabled: z.boolean(),
});

// =============================================================================
// Attachment System
// =============================================================================

const AttachmentMetadata = z.object({
  workspaceId: z.string().optional(),
  userId: z.string().optional(),
  messageId: z.string().optional(),
  thumbnailKey: z.string().optional(),
}).catchall(z.unknown());

const Attachment = z.object({
  id: z.string(),
  key: z.string(), // Storage key path
  filename: z.string(),
  contentType: z.string(),
  size: z.number().int(),
  uploadedAt: TimestampField,
  previewUrl: z.string().optional(),
  metadata: AttachmentMetadata.optional(),
});

const UploadResult = z.object({
  key: z.string(),
  filename: z.string(),
  size: z.number().int(),
  contentType: z.string(),
  uploadedAt: TimestampField,
  previewUrl: z.string().optional(),
});

// =============================================================================
// Exports
// =============================================================================

export const guiSchemas = {
  // Workspace & UI
  WorkspaceType,
  Workspace,
  NotificationType,
  Notification,
  // Data Source & File System
  DataSourceType,
  Environment,
  DataSourceMetadata,
  DataSourceCapabilities,
  DataSource,
  FileItemType,
  FileItem,
  // Search
  SearchResult,
  SearcherConfig,
  // Attachment
  AttachmentMetadata,
  Attachment,
  UploadResult,
};

export type GuiSchemaTypes = {
  [K in keyof typeof guiSchemas]: z.infer<(typeof guiSchemas)[K]>;
};
